import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { PageHeader, Empty, Badge } from "@/components/common";
import Modal from "@/components/Modal";
import { Plus, Search, Upload, Download, Pencil, Trash2, Loader2 } from "lucide-react";

const EMPTY = {
  company_name: "", position: "", location: "", linkedin_url: "",
  poc_name: "", poc_email: "", date: ""
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);

  const fetchLeads = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) { setError(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLeads(); }, []);

  const filtered = leads.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [l.company_name, l.position, l.location, l.poc_name, l.poc_email]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setError(""); setModalOpen(true); };
  const openEdit = (lead) => { setEditing(lead); setForm({ ...EMPTY, ...lead }); setError(""); setModalOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      if (editing) await api.put(`/leads/${editing.id}`, form);
      else await api.post("/leads", form);
      setModalOpen(false);
      await fetchLeads();
    } catch (e2) { setError(formatErr(e2)); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/leads/${id}`); await fetchLeads(); }
    catch (e) { alert(formatErr(e)); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${data.inserted} leads.`);
      await fetchLeads();
    } catch (e2) { alert(formatErr(e2)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "leads.csv";
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatErr(e)); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        testId="leads-header"
        title="Leads"
        subtitle="Pipeline of prospective clients and opportunities."
        action={
          <div className="flex gap-2">
            <label className="btn-secondary cursor-pointer" data-testid="import-csv-button">
              {importing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Import CSV
              <input type="file" accept=".csv" hidden onChange={handleImport} data-testid="import-csv-input" />
            </label>
            <button className="btn-secondary" onClick={handleExport} data-testid="export-csv-button">
              <Download size={16} /> Export
            </button>
            <button className="btn-primary" onClick={openCreate} data-testid="create-lead-button">
              <Plus size={16} /> New Lead
            </button>
          </div>
        }
      />

      <div className="card-base mb-4">
        <div className="p-4 flex items-center gap-3 border-b border-gray-100">
          <Search size={16} className="text-gray-400" />
          <input
            data-testid="leads-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, position, location, POC..."
            className="flex-1 outline-none text-sm bg-transparent"
          />
          <span className="text-xs text-gray-400 font-mono">{filtered.length} / {leads.length}</span>
        </div>

        {loading ? (
          <div className="p-8 text-gray-500 text-sm">Loading leads…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6"><Empty title="No leads found" subtitle="Create a new lead or import a CSV." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Company</Th>
                  <Th>Position</Th>
                  <Th>Location</Th>
                  <Th>POC</Th>
                  <Th>Date</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50 group" data-testid={`lead-row-${l.id}`}>
                    <td className="py-3 px-4 font-medium text-gray-900">{l.company_name}</td>
                    <td className="py-3 px-4 text-gray-700">{l.position}</td>
                    <td className="py-3 px-4 text-gray-700">{l.location || "—"}</td>
                    <td className="py-3 px-4">
                      <div className="text-gray-900">{l.poc_name || "—"}</div>
                      <div className="text-xs text-gray-500">{l.poc_email}</div>
                    </td>
                    <td className="py-3 px-4 text-gray-600 font-mono text-xs">{l.date || "—"}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end gap-1">
                        <button onClick={() => openEdit(l)} className="btn-ghost" data-testid={`edit-lead-${l.id}`}><Pencil size={14} /></button>
                        <button onClick={() => remove(l.id)} className="btn-ghost text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-lead-${l.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Lead" : "New Lead"} testId="lead-modal">
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Company *" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} required testId="lead-company" />
            <Field label="Position *" value={form.position} onChange={(v) => setForm({ ...form, position: v })} required testId="lead-position" />
            <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="lead-location" />
            <Field label="LinkedIn URL" value={form.linkedin_url} onChange={(v) => setForm({ ...form, linkedin_url: v })} testId="lead-linkedin" />
            <Field label="POC Name" value={form.poc_name} onChange={(v) => setForm({ ...form, poc_name: v })} testId="lead-poc-name" />
            <Field label="POC Email" value={form.poc_email} onChange={(v) => setForm({ ...form, poc_email: v })} testId="lead-poc-email" />
            <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="lead-date" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="lead-submit">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : (editing ? "Save changes" : "Create lead")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4 ${className}`}>{children}</th>;
}

function Field({ label, value, onChange, required, type = "text", testId }) {
  return (
    <label className="block">
      <span className="label-base">{label}</span>
      <input type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-base" data-testid={testId} />
    </label>
  );
}
